import React, { useDeferredValue, useMemo, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import {
  useGetWhiteLabelCompaniesQuery,
  useGetWhiteLabelCompanyQuery,
} from "../../../redux/admin/whiteLabelApi";

function byId(items = []) {
  const seen = new Map();
  items.forEach((item) => {
    if (item?._id && !seen.has(item._id)) {
      seen.set(item._id, item);
    }
  });
  return Array.from(seen.values());
}

export default function WhiteLabelCompanyAutocomplete({
  value,
  onChange,
  label = "Select Company",
  helperText = "",
  disabled = false,
  required = false,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearch = useDeferredValue(searchQuery);

  const { data: companyResults, isFetching: isFetchingCompanies } =
    useGetWhiteLabelCompaniesQuery(
      {
        page: 1,
        limit: 25,
        search: String(deferredSearch || "").trim() || undefined,
        sortBy: "companyName",
        sortOrder: "asc",
      },
      {
        refetchOnMountOrArgChange: true,
      }
    );

  const {
    data: selectedCompany,
    isFetching: isFetchingSelectedCompany,
  } = useGetWhiteLabelCompanyQuery(value, {
    skip: !value,
  });

  const options = useMemo(
    () => byId([...(companyResults?.items || []), selectedCompany].filter(Boolean)),
    [companyResults?.items, selectedCompany]
  );

  const selectedOption = useMemo(
    () => options.find((item) => item?._id === value) || null,
    [options, value]
  );

  return (
    <Autocomplete
      options={options}
      value={selectedOption}
      onChange={(_, option) => onChange(option?._id || "")}
      onInputChange={(_, nextValue, reason) => {
        if (reason === "input") {
          setSearchQuery(nextValue);
        }
      }}
      disabled={disabled}
      loading={isFetchingCompanies || isFetchingSelectedCompany}
      isOptionEqualToValue={(option, currentValue) =>
        option?._id === currentValue?._id
      }
      getOptionLabel={(option) =>
        option?.companyName
          ? `${option.companyName} (${option.companyCode})`
          : ""
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetchingCompanies || isFetchingSelectedCompany ? (
                  <CircularProgress color="inherit" size={18} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
